import { useState, useEffect, useCallback } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, MessageCircle, ChevronRight, ChevronLeft, X, ZoomIn, ShieldCheck, Droplets, Waves, Wind, Feather, Sparkles } from "lucide-react";
import { LazyImage } from "@/components/site/LazyImage";
import { Reveal } from "@/components/site/Reveal";
import { IMAGES } from "@/config/images.config";
import { getProduct, PRODUCT_CATEGORIES, SITE } from "@/config/site";

const WHY_ICONS = { shield: ShieldCheck, absorb: Droplets, retain: Waves, dry: Wind, light: Feather, soft: Sparkles };

const Chapter = ({ n, title }) => (
  <div className="mb-10 flex items-end gap-4">
    <span className="font-display text-5xl font-black leading-none text-sky/30 md:text-6xl">{n}</span>
    <h2 className="font-display text-3xl font-extrabold tracking-tight text-navy md:text-5xl">{title}</h2>
  </div>
);

export default function ProductDetail() {
  const { key } = useParams();
  const product = getProduct(key);
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);

  const media = product ? IMAGES.categories[product.key] : null;
  const images = media ? [media.hero, ...media.gallery] : [];

  const go = useCallback((dir) => setActive((a) => (a + dir + images.length) % images.length), [images.length]);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(false);
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, go]);

  if (!product) return <Navigate to="/products" replace />;

  const idx = PRODUCT_CATEGORIES.findIndex((p) => p.key === product.key);
  const next = PRODUCT_CATEGORIES[(idx + 1) % PRODUCT_CATEGORIES.length];

  const stats = [
    { label: "Typical GSM", value: product.gsm.replace(" GSM", ""), unit: "GSM" },
    { label: "Size Options", value: String(product.sizes.length), unit: "variants" },
    { label: "Weave", value: "3", unit: "Plain · Dobby · Jacquard" },
    { label: "Origin", value: "IN", unit: "Made in India" },
  ];

  return (
    <div data-testid="product-detail-page">
      {/* BREADCRUMB */}
      <div className="bg-navy px-6 pt-28 md:px-12 md:pt-32 lg:px-16">
        <div className="mx-auto flex max-w-[1600px] items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-white/40">
          <Link to="/" className="hover:text-white">Home</Link>
          <ChevronRight className="h-3 w-3" />
          <Link to="/products" data-testid="back-to-products" className="hover:text-white">Products</Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-sky">{product.name}</span>
        </div>
      </div>

      {/* HERO: plain clickable image + info */}
      <section className="bg-navy px-6 pb-24 pt-10 text-white md:px-12 md:pb-28 lg:px-16" data-testid="product-hero">
        <div className="mx-auto grid max-w-[1600px] gap-12 lg:grid-cols-2 lg:gap-16">
          {/* gallery */}
          <div className="min-w-0">
            <button
              onClick={() => setLightbox(true)}
              data-testid="hero-image-button"
              aria-label="Zoom image"
              className="group relative block aspect-[4/3] w-full cursor-zoom-in overflow-hidden rounded-[2rem] bg-black/5 transition-all hover:scale-[1.01] hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-sky focus:ring-offset-2"
            >
              <LazyImage src={images[active]} alt={product.name} className="relative z-10 h-full w-full" imgClassName="!object-cover transition-transform duration-700 group-hover:scale-105" />
              <span className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-white/90 text-navy opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                <ZoomIn className="h-5 w-5" />
              </span>
            </button>
            <div className="mt-4 flex gap-3 overflow-x-auto pb-2">
              {images.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  data-testid={`thumb-${i}`}
                  aria-label={`View image ${i + 1}`}
                  className={`h-20 w-28 shrink-0 overflow-hidden rounded-2xl border-2 bg-black/5 transition-all ${active === i ? "border-sky" : "border-white/10 opacity-60 hover:opacity-100"}`}
                >
                  <LazyImage src={src} alt="" className="h-full w-full" imgClassName="!object-cover" />
                </button>
              ))}
            </div>
          </div>

          {/* info */}
          <div className="flex min-w-0 flex-col justify-center">
            <Reveal>
              <p className="font-display text-xs font-bold uppercase tracking-[0.3em] text-sky md:text-sm">Terry Towel · {product.tagline}</p>
              <h1 className="mt-3 font-display font-black leading-[0.95] tracking-tighter text-[clamp(2.25rem,6vw,4.5rem)]">{product.name}</h1>
              <p className="mt-6 max-w-xl text-white/70 md:text-lg">{product.longDesc}</p>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center gap-2 rounded-full bg-sky px-5 py-2.5 text-sm font-bold text-navy">Typical GSM · {product.gsm}</span>
              </div>

              <div className="mt-6">
                <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-white/50">Available Sizes</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {product.sizes.map((s) => (
                    <span key={s} className="rounded-full border border-white/15 bg-white/[0.04] px-4 py-2 text-sm font-medium">{s}</span>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* AT A GLANCE STAT STRIP */}
      <section className="bg-sky px-6 py-10 md:px-12 lg:px-16">
        <div className="mx-auto grid max-w-[1600px] grid-cols-2 gap-8 md:grid-cols-4">
          {stats.map((s) => (
            <div key={s.label} className="border-l-2 border-navy/20 pl-5">
              <span className="block font-display text-xs font-bold uppercase tracking-[0.2em] text-navy/60">{s.label}</span>
              <span className="mt-1 block font-display text-3xl font-black leading-none tracking-tight text-navy md:text-4xl">{s.value}</span>
              <span className="mt-1 block text-[0.7rem] font-semibold text-navy/60">{s.unit}</span>
            </div>
          ))}
        </div>
      </section>

      {/* WHY (bamboo etc.) */}
      {product.whyFeatures && (
        <section className="mx-auto max-w-[1600px] px-6 py-24 md:px-12 md:py-28 lg:px-16" data-testid="why-features">
          <Reveal className="mb-12">
            <p className="font-display text-xs font-bold uppercase tracking-[0.24em] text-sky">The Difference</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy md:text-5xl">Why {product.name}?</h2>
          </Reveal>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {product.whyFeatures.map((f, i) => {
              const Icon = WHY_ICONS[f.icon] || Sparkles;
              return (
                <Reveal key={f.title} delay={(i % 3) * 0.08}>
                  <div className="group h-full rounded-3xl border border-black/10 bg-white p-8 transition-colors duration-300 hover:border-sky/50 hover:bg-neutral-50">
                    <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-navy text-sky transition-colors duration-300 group-hover:bg-sky group-hover:text-white"><Icon className="h-6 w-6" /></span>
                    <h3 className="mt-6 font-display text-xl font-bold leading-tight tracking-tight text-navy">{f.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-navy/55">{f.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </section>
      )}

      {/* 01 SPECIFICATIONS */}
      <section className="mx-auto max-w-[1600px] px-6 py-24 md:px-12 md:py-28 lg:px-16" data-testid="product-specs">
        <Reveal><Chapter n="01" title="Specifications" /></Reveal>
        <div className="grid gap-x-16 gap-y-0 md:grid-cols-2">
          {product.specs.map((row, i) => (
            <Reveal key={row.k} delay={(i % 2) * 0.05}>
              <div className="flex flex-col gap-1 border-b border-black/10 py-6">
                <span className="font-display text-xs font-bold uppercase tracking-[0.16em] text-sky">{row.k}</span>
                <span className="text-navy/70">{row.v}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* 02 CUSTOMIZATION + 03 YARN */}
      <section className="bg-neutral-50 px-6 py-24 md:px-12 md:py-28 lg:px-16" data-testid="product-customization">
        <div className="mx-auto grid max-w-[1600px] gap-14 lg:grid-cols-2 lg:gap-20">
          <Reveal>
            <Chapter n="02" title="Customization" />
            <div className="flex flex-wrap gap-2">
              {product.customization.map((c) => (
                <span key={c} className="rounded-full border border-navy/15 bg-white px-5 py-2.5 text-sm font-medium text-navy transition-colors hover:bg-navy hover:text-white">{c}</span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Chapter n="03" title="Yarn Options" />
            <div className="flex flex-wrap gap-2">
              {product.yarns.map((y) => (
                <span key={y} className="rounded-full border border-navy/15 bg-white px-5 py-2.5 text-sm font-medium text-navy transition-colors hover:border-sky hover:text-sky">{y}</span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="relative overflow-hidden bg-navy px-6 py-24 text-white md:px-12 md:py-28 lg:px-16">
        <div className="mx-auto flex max-w-[1600px] flex-col items-start justify-between gap-10 lg:flex-row lg:items-center">
          <div>
            <h2 className="max-w-2xl font-display text-3xl font-black leading-[1] tracking-tighter md:text-6xl">
              Need {product.name} to your exact spec?
            </h2>
            <p className="mt-5 max-w-xl text-white/60">
              Custom sizes, GSM, yarn qualities, colours and packaging — manufactured and exported worldwide by Brahmandnayak World Trade.
            </p>
          </div>
          <div className="flex shrink-0 flex-col gap-3 sm:flex-row lg:flex-col">
            <Link to="/contact" data-testid="specs-contact-cta" className="inline-flex items-center justify-center gap-2 rounded-full bg-sky px-8 py-4 text-sm font-semibold text-white transition-colors hover:bg-white hover:text-navy">
              Get Bulk Pricing <ArrowUpRight className="h-4 w-4" />
            </Link>
            <a href={SITE.whatsapp} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-8 py-4 text-sm font-semibold text-white transition-colors hover:bg-[#1ebe5b]">
              <MessageCircle className="h-4 w-4" /> Chat on WhatsApp
            </a>
          </div>
        </div>

        <div className="mx-auto mt-16 flex max-w-[1600px] justify-between border-t border-white/10 pt-8">
          <Link to="/products" className="inline-flex items-center gap-2 text-sm font-semibold text-white/60 transition-colors hover:text-white">
            <ArrowLeft className="h-4 w-4" /> All Products
          </Link>
          <Link to={`/products/${next.key}`} data-testid="next-product" className="inline-flex items-center gap-2 text-sm font-semibold text-white transition-colors hover:text-sky">
            Next: {next.name} <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* LIGHTBOX */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-navy/95 backdrop-blur-sm"
          data-testid="image-lightbox"
          onClick={() => setLightbox(false)}
        >
          <button
            onClick={(e) => { e.stopPropagation(); setLightbox(false); }}
            data-testid="lightbox-close"
            aria-label="Close"
            className="absolute right-5 top-5 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white hover:text-navy"
          >
            <X className="h-6 w-6" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); go(-1); }}
            data-testid="lightbox-prev"
            aria-label="Previous"
            className="absolute left-4 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-sky md:left-8 md:h-14 md:w-14"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <img
            src={images[active]}
            alt={product.name}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[82vh] max-w-[88vw] rounded-2xl object-contain shadow-2xl"
          />
          <button
            onClick={(e) => { e.stopPropagation(); go(1); }}
            data-testid="lightbox-next"
            aria-label="Next"
            className="absolute right-4 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-sky md:right-8 md:h-14 md:w-14"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
          <span className="absolute bottom-6 left-1/2 -translate-x-1/2 rounded-full bg-white/10 px-4 py-1.5 text-sm font-semibold text-white">
            {active + 1} / {images.length}
          </span>
        </div>
      )}
    </div>
  );
}
